/**
 * Shared focus-management helpers for panels/popovers that need to know what
 * inside them is tabbable (focus traps, Tab-into-popup bridges, etc.), and
 * which overlays currently own focus.
 */
export const FOCUSABLE_SELECTORS = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

/** Kendo's own modal surfaces, which manage focus themselves. */
export const DIALOG_SELECTOR = ".k-dialog, .k-dialog-wrapper, .k-window";

/** `SlidePanel` and everything built on it (`FormSlidePanel`, `WizardSlidePanel`). */
export const PANEL_SELECTOR = ".tlx-panel";

/** Visible, tabbable descendants of `root`, in DOM order. */
export function getFocusable(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(
    root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS),
  ).filter(el => !el.closest('[aria-hidden="true"]'));
}

/**
 * `getFocusable` minus anything that is laid out but not actually reachable.
 *
 * `offsetParent === null` catches `display: none`; the visibility check catches
 * controls that keep their box but are hidden (a common way to reveal row
 * actions without reflowing the row). `tabIndex < 0` covers anchors, which
 * match `a[href]` no matter what tabindex they carry.
 */
export function getVisibleFocusable(root: HTMLElement | null): HTMLElement[] {
  return getFocusable(root).filter(
    node =>
      node.offsetParent !== null &&
      node.tabIndex >= 0 &&
      getComputedStyle(node).visibility !== "hidden",
  );
}

/**
 * A Kendo Dialog/Modal is open somewhere.
 *
 * Dialogs portal to `document.body` outside any panel's own container and run
 * their own focus handling, so a trap underneath one must stand down entirely
 * rather than fight it for Escape and Tab.
 */
export const hasOpenDialog = (): boolean =>
  !!document.querySelector(DIALOG_SELECTOR);

/**
 * Something already owns focus and must not be interrupted by a bare-key
 * shortcut — a Kendo dialog, or one of the app's own slide panels.
 *
 * Pass `host` when the caller itself lives inside a panel: the overlay that
 * contains it is its own surface, not something layered above it.
 *
 * Distinct from `hasOpenDialog`: a panel's *own* trap cannot use this, since
 * the panel would always match itself.
 */
export const hasBlockingOverlay = (host?: Element): boolean =>
  Array.from(
    document.querySelectorAll(`${DIALOG_SELECTOR}, ${PANEL_SELECTOR}`),
  ).some(overlay => !host || !overlay.contains(host));
